import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';

export interface GoogleAccount {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
}

export interface UnifiedProperty {
  id: string;
  name: string;
  accountId: string;
  ga4Property?: {
    id: string;
    displayName: string;
  };
  gscSite?: {
    siteUrl: string;
    permissionLevel?: string;
  };
}

export interface AuthState {
  // State
  isLoading: boolean;
  isAuthenticated: boolean;
  user: GoogleAccount | null;
  googleConnection: {
    id: string;
    email: string;
    isValid: boolean;
  } | null;
  properties: UnifiedProperty[];
  activePropertyId: string | null;
  // Derived for compatibility with Sidebar
  accounts: GoogleAccount[];
  activeAccountId: string | null;

  // Actions
  initialize: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
  setActiveProperty: (propertyId: string) => void;
  refreshProperties: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isLoading: true,
      isAuthenticated: false,
      user: null,
      googleConnection: null,
      properties: [],
      activePropertyId: null,
      accounts: [],
      activeAccountId: null,

      // Initialize on app start - check session
      initialize: async () => {
        set({ isLoading: true });

        try {
          const session = await api.auth.getSession();

          if (session.authenticated && session.user) {
            const properties = session.properties || [];
            const savedPropertyId = get().activePropertyId;

            // Use saved property if it still exists, otherwise default to first
            const validSavedProperty = savedPropertyId && properties.some(p => p.id === savedPropertyId);
            const activePropertyId = validSavedProperty
              ? savedPropertyId
              : properties[0]?.id || null;

            set({
              isAuthenticated: true,
              user: session.user,
              googleConnection: session.googleConnection || null,
              properties,
              activePropertyId,
              accounts: [session.user],
              activeAccountId: session.user.id,
            });
          } else {
            set({
              isAuthenticated: false,
              user: null,
              googleConnection: null,
              properties: [],
              accounts: [],
              activeAccountId: null,
            });
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          set({
            isAuthenticated: false,
            user: null,
            googleConnection: null,
            properties: [],
            accounts: [],
            activeAccountId: null,
          });
        } finally {
          set({ isLoading: false });
        }
      },

      // Start OAuth flow - redirects to Google
      login: () => {
        // Redirect to OAuth login endpoint
        window.location.href = api.auth.getLoginUrl();
      },

      // Logout - clears session
      logout: async () => {
        set({ isLoading: true });

        try {
          await api.auth.logout();
          set({
            isAuthenticated: false,
            user: null,
            googleConnection: null,
            properties: [],
            activePropertyId: null,
            accounts: [],
            activeAccountId: null,
          });
        } catch (error) {
          console.error('Logout failed:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      // Set active property
      setActiveProperty: (propertyId) => {
        set({ activePropertyId: propertyId });
      },

      // Refresh properties from Google APIs
      refreshProperties: async () => {
        try {
          const result = await api.google.refreshProperties();
          set({ properties: result.properties || [] });
        } catch (error) {
          console.error('Failed to refresh properties:', error);
        }
      },
    }),
    {
      name: 'chartzen-web-auth',
      partialize: (state) => ({
        activePropertyId: state.activePropertyId,
      }),
    }
  )
);
